import { comparer, reaction } from 'mobx'
import { useCallback, useRef, useSyncExternalStore } from 'react'
import { deepToJS } from './deepToJS'
import { readOutsideReaction } from './readOutsideReaction'

function readSnapshot<T>(selector: () => T): T {
  return readOutsideReaction(() => deepToJS(selector()))
}

/**
 * Subscribes to a MobX observable (or derived value) and returns its current value.
 * Components using this hook re-render when the selected value changes and
 * do not need to be wrapped in `observer`.
 *
 * Pattern: expose a MobX `reaction` as a React external store while reading
 * the latest snapshot synchronously during render.
 *
 * @see https://github.com/mobxjs/mobx/discussions/3737
 *
 * @param selector - Function that reads one or more observables and returns a value.
 *   Selectors can safely return primitives, observable arrays, or observable objects.
 *   The output is converted to a plain JS snapshot via `toJS` and compared
 *   structurally (`comparer.structural`), so manual spreading is not needed.
 *
 *   **Best practice:** keep selectors narrow — select only the data you render.
 *   Returning a large observable tree causes `toJS` to deep-clone and
 *   `comparer.structural` to deep-compare on every tracked change.
 * @param dependencies - The normal react dependencies to watch.
 * @returns The current plain JS value from `selector`.
 */
/** @public */
export function useMobxSelector<T>(selector: () => T, dependencies: any[]): T {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectorCallback = useCallback(selector, dependencies)
  const snapshotRef = useRef<
    | {
        initialized: false
        selector: undefined
        value: undefined
      }
    | {
        initialized: true
        selector: () => T
        value: T
      }
  >({
    initialized: false,
    selector: undefined,
    value: undefined,
  })

  const updateSnapshot = useCallback((nextValue: T, selector: () => T): T => {
    const currentSnapshot = snapshotRef.current
    if (
      !currentSnapshot.initialized ||
      currentSnapshot.selector !== selector ||
      !comparer.structural(currentSnapshot.value, nextValue)
    ) {
      snapshotRef.current = {
        initialized: true,
        selector,
        value: nextValue,
      }
      return nextValue
    }

    return currentSnapshot.value
  }, [])

  const getSnapshot = useCallback(() => {
    const currentSnapshot = snapshotRef.current
    if (
      currentSnapshot.initialized &&
      currentSnapshot.selector === selectorCallback
    ) {
      return currentSnapshot.value
    }

    return updateSnapshot(readSnapshot(selectorCallback), selectorCallback)
  }, [selectorCallback, updateSnapshot])

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const handleSnapshotChange = (plainValue: T) => {
        const currentSnapshot = snapshotRef.current
        if (
          currentSnapshot.initialized &&
          currentSnapshot.selector === selectorCallback &&
          comparer.structural(currentSnapshot.value, plainValue)
        ) {
          return
        }

        snapshotRef.current = {
          initialized: true,
          selector: selectorCallback,
          value: plainValue,
        }
        onStoreChange()
      }

      const dispose = reaction(
        () => deepToJS(selectorCallback()),
        handleSnapshotChange
      )

      handleSnapshotChange(readSnapshot(selectorCallback))

      return dispose
    },
    [selectorCallback]
  )

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
